import { useMemo } from "react";
import type { DeepReadonly } from "ts-essentials";

import { readState } from "@/__main__/app-state.mjs";
import type { PaymentMethod } from "@/feature-wallet/models/payment-method.mjs";
import type { PaymentProviderType } from "@/feature-wallet/models/payment-method-provider.mjs";
import type { UserPaymentProvider } from "@/feature-wallet/models/user-payment-methods.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

/**
 * Returns the payment method with the given id. If no payment method is found, returns null.
 *
 * In some cases, (i.e. Braintree Subscriptions), the payment method ID does not correspond
 * to the payment method itself and is instead a different ID entirely. To handle this properly,
 * the `providerType` must also be provided.
 */
export function getPaymentMethod({
  paymentProviders,
  providerType,
  paymentMethodId,
}: {
  paymentProviders: DeepReadonly<UserPaymentProvider[]>;
  providerType?: PaymentProviderType | null;
  paymentMethodId: string | null;
}): DeepReadonly<PaymentMethod> | null {
  for (const paymentProvider of paymentProviders.filter(
    (provider) => !providerType || provider.providerType === providerType,
  )) {
    for (const paymentMethod of paymentProvider.paymentMethods) {
      if (paymentMethod.id === paymentMethodId) {
        return paymentMethod;
      }
    }
  }

  if (providerType === "BRAINTREE") {
    const paymentMethod = paymentProviders.find(
      (provider) => provider.providerType === "BRAINTREE",
    )?.paymentMethods[0];

    if (paymentMethod) {
      return paymentMethod;
    }
  }

  return null;
}

export function usePaymentMethod({
  providerType,
  paymentMethodId,
}: {
  providerType?: PaymentProviderType | null;
  paymentMethodId: string | null;
}): DeepReadonly<PaymentMethod> | null {
  const { paymentProviders } = useSnapshot(readState.wallet);

  return useMemo(
    () => getPaymentMethod({ paymentProviders, providerType, paymentMethodId }),
    [paymentProviders, paymentMethodId],
  );
}

export function usePaymentMethods(): DeepReadonly<PaymentMethod[]> | null {
  const { paymentProviders } = useSnapshot(readState.wallet);

  return useMemo(
    () =>
      paymentProviders.flatMap<PaymentMethod>(
        ({ paymentMethods }) => paymentMethods,
      ),
    [paymentProviders],
  );
}
